import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  Typography,
  Skeleton,
  Stack,
  Button,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  useMediaQuery,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { tokens } from "../../theme";
import { formatPlainNumber } from "../..//utils/appUtils";

export interface TableColumn<T> {
  id: string;
  label: string;
  minWidth?: number;
  maxWidth?: number;
  align?: "left" | "right" | "center";
  defaultHidden?: boolean;
  format?: (value: unknown, row: T) => React.ReactNode;
}

export interface TableTotals {
  label: string;
  value: number;
  prefix?: string; // e.g. "$" or "KES"
  color?: "primary" | "secondary" | "error" | "success" | "info" | "warning";
}

export interface TableStatusMetric {
  label: string;
  value: string | number;
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "success"
    | "info"
    | "warning";
}

export interface StandardTableProps<T> {
  columns: TableColumn<T>[];
  data?: T[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  page?: number;
  count?: number;
  rowsPerPage?: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  rowAction?: (row: T) => React.ReactNode;
  enableColumnManagement?: boolean;
  tableTotals?: TableTotals[];
  statusMetrics?: TableStatusMetric[];
}

const StandardTable = <T extends { id: string | number }>({
  columns,
  data = [],
  loading = false,
  error = null,
  onRetry,
  page = 1,
  count = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  rowAction,
  enableColumnManagement = false,
  tableTotals = [],
  statusMetrics = [],
}: StandardTableProps<T>): React.ReactElement => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const muiPage = page > 0 ? page - 1 : 0;

  const headerBgColor =
    theme.palette.mode === "dark"
      ? colors.primary[600]
      : theme.palette.grey[100];

  const headerTextColor =
    theme.palette.mode === "dark"
      ? colors.grey[100]
      : theme.palette.primary.main;

  const [prevColumns, setPrevColumns] = useState<TableColumn<T>[]>(columns);
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>(() =>
    columns.filter((c) => !c.defaultHidden).map((c) => c.id),
  );

  if (columns !== prevColumns) {
    setPrevColumns(columns);
    setVisibleColumnIds(
      columns.filter((c) => !c.defaultHidden).map((c) => c.id),
    );
  }

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleColumnsClick = (event: React.MouseEvent<HTMLElement>): void =>
    setAnchorEl(event.currentTarget);
  const handleColumnsClose = (): void => setAnchorEl(null);

  const toggleColumnVisibility = (columnId: string): void => {
    setVisibleColumnIds((prevIds) =>
      prevIds.includes(columnId)
        ? prevIds.filter((id) => id !== columnId)
        : [...prevIds, columnId],
    );
  };

  const visibleColumns = columns.filter((col) =>
    visibleColumnIds.includes(col.id),
  );

  const handleChangePage = (_event: unknown, newPage: number): void =>
    onPageChange(newPage + 1);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => onRowsPerPageChange(parseInt(event.target.value, 10));

  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        {[...Array(isMobile ? 3 : 5)].map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={isMobile ? 150 : 50}
            sx={{ mb: 1, borderRadius: theme.appRadius?.md || 1 }}
          />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 6,
          textAlign: "center",
          bgcolor: "background.paper",
          borderRadius: theme.appRadius?.md || 1,
          border: `1px dashed ${theme.palette.error.main}`,
        }}
      >
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <Box
            sx={{
              p: 2,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h6" color="text.primary">
            Unable to load data
          </Typography>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
          {onRetry && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<RefreshIcon />}
              onClick={onRetry}
            >
              Try Again
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          bgcolor: "background.paper",
          borderRadius: theme.appRadius?.md || 1,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No records found
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Try adjusting your filters.
        </Typography>
      </Box>
    );
  }

  const renderTableToolbar = (): React.ReactNode => {
    // Only render toolbar if we have columns to manage, totals, or statuses
    if (
      !enableColumnManagement &&
      tableTotals.length === 0 &&
      statusMetrics.length === 0
    )
      return null;

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          bgcolor: headerBgColor,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        {/* LEFT SIDE: Totals & Metrics */}
        <Stack
          direction="row"
          spacing={3}
          sx={{ flexWrap: "wrap", gap: 1, alignItems: "center" }}
        >
          {/* Render Totals */}
          {tableTotals.map((t, idx) => (
            <Box
              key={`tot-${idx}`}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Typography
                variant="caption"
                sx={{ color: alpha(headerTextColor, 0.7), fontWeight: "bold" }}
              >
                {t.label.toUpperCase()}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  color: t.color ? `${t.color}.main` : headerTextColor,
                }}
              >
                {t.prefix ? `${t.prefix} ` : ""}
                {formatPlainNumber(t.value)}
              </Typography>
            </Box>
          ))}

          {/* Vertical Divider if both exist */}
          {tableTotals.length > 0 && statusMetrics.length > 0 && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: alpha(headerTextColor, 0.2) }}
            />
          )}

          {/* Render Status Metrics */}
          {statusMetrics.map((s, idx) => (
            <Chip
              key={`stat-${idx}`}
              label={`${s.label}: ${s.value}`}
              size="small"
              color={s.color || "default"}
            />
          ))}
        </Stack>

        {/* RIGHT SIDE: Column Management */}
        {enableColumnManagement && !isMobile && (
          <Box>
            <Tooltip title="Manage Columns">
              <IconButton
                onClick={handleColumnsClick}
                size="small"
                sx={{ color: headerTextColor }}
              >
                <ViewColumnIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleColumnsClose}
            >
              <Typography
                variant="overline"
                sx={{ px: 2, pb: 1, color: "text.secondary" }}
              >
                Visible Columns
              </Typography>
              {columns.map((col) => (
                <MenuItem
                  key={col.id}
                  dense
                  onClick={() => toggleColumnVisibility(col.id)}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={visibleColumnIds.includes(col.id)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">{col.label}</Typography>}
                    sx={{ m: 0, pointerEvents: "none" }}
                  />
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </Box>
    );
  };

  if (isMobile) {
    return (
      <Box>
        <Stack spacing={2}>
          {data.map((row, index) => (
            <Card
              key={row.id || index}
              elevation={2}
              sx={{
                bgcolor: "background.paper",
                borderRadius: theme.appRadius?.md || 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                {visibleColumns.map((col) => {
                  const value = row[col.id as keyof T];
                  return (
                    <Box
                      key={col.id}
                      sx={{
                        mb: 1.5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          textTransform: "uppercase",
                          mr: 2,
                          mt: 0.5,
                          fontWeight: 700,
                        }}
                      >
                        {col.label}
                      </Typography>
                      <Box sx={{ textAlign: "right", maxWidth: "60%" }}>
                        {col.format ? (
                          col.format(value, row)
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {String(value)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                {rowAction && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      {rowAction(row)}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
        <TablePagination
          rowsPerPageOptions={[10, 25]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={muiPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ mt: 2, border: "none", bgcolor: "transparent" }}
        />
      </Box>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        width: "100%",
        overflow: "hidden",
        borderRadius: theme.appRadius?.lg || 1,
      }}
    >
      {renderTableToolbar()}
      <TableContainer
        sx={{
          maxHeight: "calc(100vh - 300px)",
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Table
          stickyHeader
          sx={{ "& .MuiTableCell-root": { borderBottom: "none" } }}
        >
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || "left"}
                  style={{
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                  }}
                  sx={{
                    bgcolor: headerBgColor,
                    color: headerTextColor,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              {rowAction && (
                <TableCell
                  align="center"
                  sx={{
                    bgcolor: headerBgColor,
                    color: headerTextColor,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                {visibleColumns.map((column) => {
                  const value = row[column.id as keyof T];
                  return (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                      }}
                    >
                      {column.format
                        ? column.format(value, row)
                        : String(value)}
                    </TableCell>
                  );
                })}
                {rowAction && (
                  <TableCell align="center">{rowAction(row)}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={muiPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: "inherit",
        }}
      />
    </Paper>
  );
};

export default StandardTable;
